import React, { useMemo } from 'react';
import type { CSSProperties } from 'react';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/prism';
import type SyntaxHighlighterProps from 'react-syntax-highlighter';
import { coy as colorScheme, darcula as darkColorScheme } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import remarkParse from 'remark-parse';
import remarkStringify from 'remark-stringify';
import unified from 'unified';
import styled from 'types/theme.types';
import { observer } from 'mobx-react';
import { useTheme } from 'hooks/use-theme';

const codeStyleOverrides: CSSProperties = {
  padding: '1em',
  borderRadius: '0.2em'
};

const Markdown: React.FunctionComponent<{
  markdown: string;
  className?: string;
  dataE2e?: string;
}> = ({ markdown, className, dataE2e }) => {
  const { appTheme, isDarkTheme } = useTheme();

  const customStyle = useMemo(
    () => ({
      ...codeStyleOverrides,
      border: `1px solid ${appTheme.lightGray}`,
      backgroundColor: appTheme.markdownBackground
    }),
    [appTheme]
  );

  const normalizedMarkdown = useMemo(() => normalizeLineBreaks(markdown), [markdown]);

  return (
    // https://github.com/remarkjs/react-markdown#use-custom-components-syntax-highlight
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      linkTarget="_blank"
      className={className}
      components={{
        table: ({ node, ...props }) => <StyledTable {...props} />,
        code({ node, inline, className: rawClassName, children, ref, ...props }) {
          const className = rawClassName as string;
          const match = /language-(\w+)/.exec(className || '');
          return !inline && match ? (
            <SyntaxHighlighter
              style={isDarkTheme ? darkColorScheme : colorScheme}
              language={match[1]}
              PreTag="div"
              data-e2e={dataE2e}
              {...(props as Partial<SyntaxHighlighterProps>)}
              customStyle={customStyle}>
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        }
      }}>
      {normalizedMarkdown}
    </ReactMarkdown>
  );
};

export default observer(Markdown);

function normalizeLineBreaks(markdown: string) {
  const tree = unified().use(remarkParse).parse(markdown);
  return unified().use(remarkStringify).stringify(tree);
}

const StyledTable = styled.table`
  width: max-content;
  max-width: 100%;
  overflow: auto;
  border-collapse: collapse;
  margin-bottom: 10px;

  tr:nth-child(2n) {
    background-color: ${({ theme }) => theme.plainGray};
  }

  th {
    text-align: center !important;
  }

  th,
  td {
    padding: 6px 13px;
    border: 1px solid ${({ theme }) => theme.lightGray};
  }
`;
